import React, { useEffect, useState } from 'react';
import EditIcon from '../../assets/EditIcon';
import ReusableButton from './Button/Button';
import CheckIcon from '../../assets/CheckIcon';
import CrossIcon from '../../assets/CrossIcon';
import calenderApi from '../../api/calenderApi';
import { IAvailabilityStatus } from '../../type/availabityStatus';
import { IMyAvailabilityPayload } from '../../type/myAvailabilityPayload';
import useUserStore from '../../store/userStore';
import { toast } from 'react-toastify';
import { translate } from '../../i18n';
import { showOrReplaceToast } from '../../utils/utility';
import { CUSTOM_TOAST_ID } from '../../constants/constant';
import EditIconDark from '../../assets/EditIconDark';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';

interface AvailabilityStatusCardProps {
  selectedDates: string[];
  fetchAvailability: () => void;
  setSelectedDates: React.Dispatch<React.SetStateAction<string[]>>;
  editMode: boolean;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function AvailabilityStatusCard({
  selectedDates,
  fetchAvailability,
  setSelectedDates,
  editMode,
  setEditMode,
}: AvailabilityStatusCardProps) {
  const staffId = useUserStore((state) => state?.user?.staffId);
  const [selected, setSelected] = useState('');
  const [availabilityOptions, setAvailabilityOptions] = useState<
    IAvailabilityStatus[]
  >([]);

  const fetchStatusOptions = async () => {
    try {
      const response = await calenderApi?.getCalendarAvailabilityStatus();
      setAvailabilityOptions(response?.data);
    } catch (error) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        translate('myAvalabilityCalender.failedToFetch'),
        'error',
      );
    }
  };

  useEffect(() => {
    fetchStatusOptions();
  }, []);
  const styleToColorClass: Record<string, string> = {
    available: 'bg-availability-green',
    maybe: 'bg-availability-yellow',
    nochoice: 'bg-availability-gray',
    unavailable: 'bg-availability-red',
    annualleave: 'bg-availability-blue',
    sick: 'bg-availability-lightyellow',
  };

  const handleConfirm = async () => {
    if (!staffId) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        translate('myAvalabilityCalender.staffMissing'),
        'error',
      );
      return;
    }

    if (selectedDates?.length === 0) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        translate('myAvalabilityCalender.selectDate'),
        'error',
      );
      return;
    }

    const selectedOption = availabilityOptions?.find(
      (opt) => opt?.abbrev === selected,
    );
    if (!selectedOption) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        translate('myAvalabilityCalender.emptyStatus'),
        'error',
      );

      return;
    }

    const payload: IMyAvailabilityPayload[] = selectedDates?.map((date) => ({
      workDate: date.toString().split('T')[0],
      staffId: staffId,
      statusId: selectedOption?.availabilityStatusId,
    }));

    try {
      await calenderApi.updateCalendarAvailabilityStatus(payload);
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        translate('myAvalabilityCalender.updateStatus'),
        'success',
      );
      setEditMode(false);
      fetchAvailability();
      setSelectedDates([]);
    } catch (error) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        translate('myAvalabilityCalender.failedToUpdate'),
        'error',
      );
    }
  };

  return (
    <div className="w-full border border-customBorder-border rounded-lg p-4 shadow-md bg-backgroundColor-darkThemeColor">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl text-text-primary font-semibold">
          {translate('myAvalabilityCalender.availabilityStatus')}
        </h2>
        <button
          onClick={() => setEditMode(!editMode)}
          className="text-gray hover:text-gray"
        >
          <div>
            <div className="block dark:hidden">
              <EditIcon />
            </div>
            <div className="hidden dark:block">
              <EditIconDark />
            </div>
          </div>
          {/* <EditIcon /> */}
        </button>
      </div>
      <div className="p-4 border-t-2 rounded-b-md border-light-blue" />
      <p className="text-text-primary mb-4 font-normal text-sm">
        {translate('myAvalabilityCalender.subtitle')}
      </p>

      <div className="space-y-3">
        {availabilityOptions?.map((option, index) => (
          <div key={index} className="flex items-center gap-3">
            {editMode && (
              <input
                type="radio"
                name="availability"
                value={option?.abbrev}
                checked={selected === option?.abbrev}
                onChange={() => setSelected(option?.abbrev)}
                className="accent-blue border"
              />
            )}
            <div className="flex items-center gap-3">
              <span
                className={`rounded flex items-center justify-center px-8 py-2 text-light-black font-bold text-sm ${
                  option?.style
                    ? styleToColorClass[option?.style] || 'bg-gray'
                    : 'bg-gray'
                }`}
              >
                {option?.abbrev}
              </span>
              <label className="text-text-primary font-normal text-sm">
                {option?.statusName}
              </label>
            </div>
          </div>
        ))}
      </div>

      {editMode && (
        <div className="col-span-2 flex mt-4">
          <div className="flex w-full justify-end gap-2">
            <ReusableButton
              variant="secondary"
              children={'Cancel'}
              icon={
                  <div>
                    <div className="block dark:hidden">
                      <CrossIcon />
                    </div>
                    <div className="hidden dark:block">
                      <CrossIconDarkForm />
                    </div>
                  </div>
                }
              className="w-auto bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
              onClick={() => setEditMode(false)}
            />
            <ReusableButton
              icon={<CheckIcon />}
              children={'Save'}
              variant="primary"
              className="w-auto bg-background-backgroundColor"
              onClick={handleConfirm}
            />
          </div>
        </div>
      )}
    </div>
  );
}
